/**
 * ModelingMode — scenario planning with live projections.
 *
 * Left column (30%): AssumptionsPanel with sliders
 * Right column (70%): ProjectionPanel (P&L, BS, CF, KPIs)
 * Below: CashForecastChart + ScenarioComparison
 * Bottom bar: Save, Export, Reset, Compare
 */

import { useState, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../../api'
import { cn } from '../../lib/utils'
import { Save, Download, BarChart3, ChevronDown, Plus, Loader2 } from 'lucide-react'
import { useProjection } from '../../hooks/useProjection'
import type { BaseActuals, Assumptions } from '../../services/projectionEngine'
import { AssumptionsPanel } from './AssumptionsPanel'
import { ProjectionPanel } from './ProjectionPanel'
import { CashForecastChart } from './CashForecastChart'
import { ScenarioComparison } from './ScenarioComparison'
import { SensitivityAnalysis } from './SensitivityAnalysis'
import { MonteCarloSimulation } from './MonteCarloSimulation'

interface ModelingModeProps {
  orgId?: number
  entityCode?: string
  periodCode?: string
  className?: string
}

export function ModelingMode({ entityCode = '*', periodCode, className }: ModelingModeProps) {
  const queryClient = useQueryClient()
  const [showComparison, setShowComparison] = useState(false)
  const [selectedScenarioId, setSelectedScenarioId] = useState<number | null>(null)
  const [saveName, setSaveName] = useState('')
  const [showSaveDialog, setShowSaveDialog] = useState(false)

  // Load base actuals from the metrics dashboard
  const { data: dashboardData } = useQuery({
    queryKey: ['financial-dashboard', entityCode, periodCode],
    queryFn: () => api.get('/metrics/financial/dashboard', {
      params: { entity_code: entityCode, period_code: periodCode || undefined },
    }).then(r => r.data),
    staleTime: 60_000,
  })

  // Transform dashboard metrics into BaseActuals
  const baseActuals: BaseActuals | null = dashboardData?.metrics
    ? extractBaseActuals(dashboardData.metrics)
    : null

  // Load assumptions catalog
  const { data: catalogData } = useQuery({
    queryKey: ['assumptions-catalog'],
    queryFn: () => api.get('/metrics/assumptions-catalog').then(r => r.data),
    staleTime: 300_000,
  })

  // Load saved scenarios
  const { data: scenariosData } = useQuery({
    queryKey: ['scenarios'],
    queryFn: () => api.get('/metrics/scenarios').then(r => r.data),
    staleTime: 30_000,
  })

  // Projection hook — instant recomputation on assumption change
  const {
    assumptions, projection, cashForecast,
    updateAssumption, resetAssumptions, loadAssumptions,
  } = useProjection(baseActuals)

  // Load scenario into sliders when selected
  useEffect(() => {
    if (selectedScenarioId && scenariosData?.scenarios) {
      const scenario = scenariosData.scenarios.find((s: any) => s.id === selectedScenarioId)
      if (scenario?.assumptions) {
        loadAssumptions(scenario.assumptions)
      }
    }
  }, [selectedScenarioId, scenariosData, loadAssumptions])

  // Save scenario mutation
  const saveMutation = useMutation({
    mutationFn: (payload: { name: string; assumptions: Assumptions; computed_outputs: any }) =>
      api.post('/metrics/scenarios', {
        name: payload.name,
        scenario_type: 'CUSTOM',
        assumptions: payload.assumptions,
        computed_outputs: payload.computed_outputs,
        base_period: periodCode || undefined,
      }).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenarios'] })
      setShowSaveDialog(false)
      setSaveName('')
    },
  })

  // Export board package mutation — POST returns PPTX file directly
  const exportMutation = useMutation({
    mutationFn: async (payload: { scenario_id?: number; period_code?: string }) => {
      const res = await api.post('/metrics/workspace/board-package', payload, {
        responseType: 'blob',
      })
      // Extract filename from Content-Disposition header or use default
      const disposition = res.headers['content-disposition'] || ''
      const match = disposition.match(/filename="?([^";\n]+)"?/)
      const filename = match?.[1] || 'Board_Package.pptx'
      // Trigger browser download
      const url = window.URL.createObjectURL(res.data)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    },
  })

  const handleSave = useCallback(() => {
    if (!saveName.trim()) return
    saveMutation.mutate({
      name: saveName.trim(),
      assumptions,
      computed_outputs: projection,
    })
  }, [saveName, assumptions, projection, saveMutation])

  const handleExport = useCallback(() => {
    exportMutation.mutate({
      scenario_id: selectedScenarioId || undefined,
      period_code: periodCode || undefined,
    })
  }, [selectedScenarioId, periodCode, exportMutation])

  const scenarios = scenariosData?.scenarios || []
  const catalog = catalogData?.assumptions || []

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {/* Toolbar */}
      <div className={cn(
        'flex items-center justify-between gap-3 rounded-lg',
        'bg-[var(--bg-surface,var(--bg-surface))] border border-white/[0.06]',
        'px-4 py-2.5',
      )}>
        {/* Scenario selector */}
        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value={selectedScenarioId ?? ''}
              onChange={e => setSelectedScenarioId(e.target.value ? Number(e.target.value) : null)}
              className={cn(
                'appearance-none bg-[var(--bg-elevated,var(--bg-elevated))] border border-white/10',
                'rounded-md px-3 py-1.5 pr-7 text-[12px]',
                'text-[var(--text-primary,#F0F2F5)]',
                'focus:outline-none focus:border-[var(--accent,var(--accent))]',
              )}
            >
              <option value="">New Scenario</option>
              {scenarios.map((s: any) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-[var(--text-muted,#64748B)] pointer-events-none" />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowComparison(!showComparison)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px]',
              'border border-white/10 text-[var(--text-secondary,#94A3B8)]',
              'hover:bg-white/[0.04] transition-colors',
              showComparison && 'border-[var(--accent,var(--accent))] text-[var(--accent,var(--accent))]',
            )}
          >
            <BarChart3 className="h-3.5 w-3.5" />
            Compare
          </button>

          {showSaveDialog ? (
            <div className="flex items-center gap-1.5">
              <input
                type="text"
                placeholder="Scenario name..."
                value={saveName}
                onChange={e => setSaveName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSave()}
                autoFocus
                className={cn(
                  'bg-[var(--bg-elevated,var(--bg-elevated))] border border-white/10 rounded-md',
                  'px-2 py-1.5 text-[11px] text-[var(--text-primary,#F0F2F5)]',
                  'focus:outline-none focus:border-[var(--accent,var(--accent))] w-40',
                )}
              />
              <button
                onClick={handleSave}
                disabled={!saveName.trim() || saveMutation.isPending}
                className={cn(
                  'px-2.5 py-1.5 rounded-md text-[11px] font-medium',
                  'bg-[var(--accent,var(--accent))] text-white',
                  'hover:bg-[#05a5c1] transition-colors',
                  'disabled:opacity-50',
                )}
              >
                {saveMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
              </button>
              <button
                onClick={() => { setShowSaveDialog(false); setSaveName('') }}
                className="text-[11px] text-[var(--text-hint,#475569)] hover:text-[var(--text-secondary,#94A3B8)]"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowSaveDialog(true)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px]',
                'border border-white/10 text-[var(--text-secondary,#94A3B8)]',
                'hover:bg-white/[0.04] transition-colors',
              )}
            >
              <Save className="h-3.5 w-3.5" />
              Save
            </button>
          )}

          <button
            onClick={handleExport}
            disabled={exportMutation.isPending}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium',
              'bg-[var(--accent,var(--accent))] text-white',
              'hover:bg-[#05a5c1] transition-colors',
              'disabled:opacity-50',
            )}
          >
            {exportMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
            Board Package
          </button>
        </div>
      </div>

      {/* Main content: assumptions (left) + projections (right) */}
      <div className="grid grid-cols-[320px_1fr] gap-4">
        {/* Left: Assumptions */}
        <div className={cn(
          'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))]',
          'p-4 overflow-y-auto max-h-[700px]',
        )}>
          <AssumptionsPanel
            assumptions={assumptions}
            catalog={catalog}
            onUpdate={updateAssumption}
            onReset={resetAssumptions}
          />
        </div>

        {/* Right: Projections */}
        <div className="overflow-y-auto max-h-[700px]">
          <ProjectionPanel
            projection={projection}
            baseActuals={baseActuals}
          />
        </div>
      </div>

      {/* Cash Forecast Chart */}
      <CashForecastChart forecast={cashForecast} />

      {/* Sensitivity Analysis */}
      {baseActuals && (
        <div className={cn(
          'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))] p-5',
        )}>
          <div className="flex items-center gap-1.5 mb-4">
            <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            <span className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)]">
              Sensitivity Analysis
            </span>
          </div>
          <SensitivityAnalysis
            assumptions={assumptions}
            catalog={catalog}
            baseActuals={baseActuals}
          />
        </div>
      )}

      {/* Monte Carlo Simulation */}
      {baseActuals && (
        <div className={cn(
          'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))] p-5',
        )}>
          <MonteCarloSimulation
            assumptions={assumptions}
            catalog={catalog}
            baseActuals={baseActuals}
          />
        </div>
      )}

      {/* Scenario Comparison (toggled) */}
      {showComparison && (
        <ScenarioComparison
          scenarios={scenarios}
          currentProjection={projection}
        />
      )}
    </div>
  )
}

/** Extract BaseActuals from the metrics dashboard response */
function extractBaseActuals(metrics: any[]): BaseActuals {
  const map: Record<string, number> = {}
  for (const m of metrics) {
    if (m.metric_code && m.numeric_value != null) {
      map[m.metric_code] = m.numeric_value
    }
  }
  return {
    revenue: map.revenue || 0,
    cogs: map.cogs || 0,
    operating_expenses: map.operating_expenses || 0,
    total_expenses: map.total_expenses || 0,
    net_income: map.net_income || 0,
    total_assets: map.total_assets || 0,
    current_assets: map.current_assets || 0,
    ar_balance: map.ar_balance || 0,
    total_liabilities: map.total_liabilities || 0,
    current_liabilities: map.current_liabilities || 0,
    ap_balance: map.ap_balance || 0,
    total_equity: map.total_equity || 0,
    working_capital: map.working_capital || 0,
    cash: map.cash,
  }
}
